import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import translations from "../i18n/translations";
import * as programsService from "../services/programs";
import "./ProgramLevelSelect.css";

export default function ProgramLevelSelect() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const isTeacher = Boolean(user && user.role === "teacher");
  const isAdmin = Boolean(
    user && (user.role === "admin" || user.role === "superadmin"),
  );
  const isParent = Boolean(user && user.role === "parent");

  useEffect(() => {
    if (!programId) return;

    if (isTeacher || isAdmin) {
      navigate(`/programs/${programId}?edit=1`, { replace: true });
      return;
    }

    if (user && !isParent) {
      navigate(`/programs/${programId}`, { replace: true });
    }
  }, [isAdmin, isParent, isTeacher, navigate, programId, user]);

  const asArray = (value) => (Array.isArray(value) ? value : []);

  const catalogLevels = useMemo(
    () =>
      asArray(
        t("landingPage.levels.items", { returnObjects: true, lng: language }),
      ),
    [t, language],
  );

  const [programTitle, setProgramTitle] = useState("");
  const [programLevels, setProgramLevels] = useState([]);

  const normalizeProgramLevels = (value) => {
    const raw = Array.isArray(value) ? value : [];
    const normalized = [];
    const seen = new Set();
    raw.forEach((item) => {
      if (item == null) return;
      if (typeof item === "string") {
        const grade = String(item).trim();
        if (!grade || seen.has(grade)) return;
        seen.add(grade);
        normalized.push({ grade, label: "", description: "", image_url: "" });
        return;
      }
      if (typeof item === "object") {
        const grade = String(item.grade ?? item.id ?? "").trim();
        if (!grade || seen.has(grade)) return;
        seen.add(grade);
        normalized.push({
          grade,
          label: String(item.label ?? item.title ?? "").trim(),
          description: String(item.description ?? "").trim(),
          image_url: String(item.image_url ?? item.image ?? "").trim(),
        });
      }
    });
    return normalized;
  };

  useEffect(() => {
    let ignore = false;
    async function loadProgramTitle() {
      try {
        const payload = await programsService.fetchPrograms();
        const list = Array.isArray(payload?.programs) ? payload.programs : [];
        const found = list.find(
          (p) => p?.slug === programId || String(p?.id) === String(programId),
        );
        if (!ignore) {
          setProgramTitle(String(found?.title || ""));
          const directLevels = normalizeProgramLevels(found?.levels);
          if (directLevels.length) {
            setProgramLevels(directLevels);
          } else {
            // If the program doesn't have stored levels yet, fall back to the catalog.
            setProgramLevels(
              asArray(catalogLevels)
                .map((item) => ({
                  grade: String(item?.grade ?? "").trim(),
                  label: String(item?.label ?? "").trim(),
                  description: "",
                  image_url: "",
                }))
                .filter((lvl) => lvl.grade),
            );
          }
        }
      } catch (e) {
        if (!ignore) {
          setProgramTitle("");
          setProgramLevels(
            asArray(catalogLevels)
              .map((item) => ({
                grade: String(item?.grade ?? "").trim(),
                label: String(item?.label ?? "").trim(),
                description: "",
                image_url: "",
              }))
              .filter((lvl) => lvl.grade),
          );
        }
      }
    }

    if (programId) {
      loadProgramTitle();
    }

    return () => {
      ignore = true;
    };
  }, [catalogLevels, programId]);

  const handlePickLevel = (level) => {
    const grade = String(level?.grade ?? "").trim();
    if (!grade) return;
    navigate(`/programs/${programId}?level=${encodeURIComponent(grade)}`);
  };

  return (
    <div className="program-level-select" dir={dir}>
      <div className="pp-shell">
        <div className="program-level-select__head">
          <div>
            <span className="pp-section-tag pp-section-tag--desert">
              {t("landingPage.levels.tag")}
            </span>
            <h1 className="program-level-select__title">
              {t("programLevelSelect.title")}
            </h1>
            <p className="program-level-select__subtitle">
              {programTitle
                ? t("programLevelSelect.subtitle.forProgram", {
                    title: programTitle,
                  })
                : t("programLevelSelect.subtitle.continue")}
            </p>
          </div>
          <div className="program-level-select__actions">
            <Link to="/" className="btn ghost">
              {t("programLevelSelect.actions.backToHome")}
            </Link>
          </div>
        </div>

        <section className="pp-desert pp-desert--compact">
          <div className="pp-desert__intro">
            <h3>{t("landingPage.levels.title")}</h3>
            <p>{t("landingPage.levels.description")}</p>
          </div>

          <div className="pp-levels">
            {programLevels.map((level, index) => (
              <button
                key={level.grade}
                type="button"
                onClick={() => handlePickLevel(level)}
                className={`pp-level-card level-accent-${1 + (index % 9)}`}
                aria-label={`${t("landingPage.levels.ariaPrefix")} ${level.grade}`}
              >
                <span className="pp-level-number">{level.grade}</span>
                <p>
                  {level.label ||
                    catalogLevels.find(
                      (item) =>
                        String(item?.grade ?? "").trim() ===
                        String(level.grade).trim(),
                    )?.label ||
                    ""}
                </p>
              </button>
            ))}
          </div>

          {!programLevels.length ? (
            <p className="pp-desert__note">
              {t("programLevelSelect.states.noLevels")}
            </p>
          ) : null}

          <p className="pp-desert__note">{t("landingPage.levels.note")}</p>
        </section>
      </div>
    </div>
  );
}
